import React, { useState, useEffect, useCallback } from 'react';
import { adminApi } from '../../services/api';
import { PageTooltip } from '../components/TooltipSystem';
import {
  CATEGORY_META, ALL_CATEGORIES,
} from '../components/timeline/timelineUtils';
import { Plus, Trash2, EyeOff, Eye, Calendar as CalendarIcon, BookmarkPlus } from 'lucide-react';
import { toast } from 'sonner';

/**
 * "Eventi importanti" — flat list of every business-significant action
 * (broadcasts, gifts, campaigns, coupons, events, menu changes, manual
 * annotations) for the current brand, with category-chip filtering and a
 * minimal create/delete UI for manual annotations.
 */
export default function AdminTimeline() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(ALL_CATEGORIES);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', occurred_at: '', category: 'manual',
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    adminApi.get('/admin/timeline?limit=500')
      .then((r) => setEvents(r.data?.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleCat = (cat) => {
    setFilters((prev) => prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]);
  };

  const submitAnnotation = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Titolo obbligatorio');
      return;
    }
    setSaving(true);
    try {
      const body = {
        title: form.title.trim(),
        description: form.description.trim(),
        category: form.category,
      };
      if (form.occurred_at) {
        // Convert local datetime-local to ISO (assumes local tz)
        body.occurred_at = new Date(form.occurred_at).toISOString();
      }
      await adminApi.post('/admin/timeline/annotation', body);
      toast.success('Annotazione aggiunta');
      setForm({ title: '', description: '', occurred_at: '', category: 'manual' });
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('Eliminare questa annotazione?')) return;
    try {
      await adminApi.delete(`/admin/timeline/${id}`);
      toast.success('Eliminato');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Eliminazione fallita');
    }
  };

  const toggleChartVisibility = async (id) => {
    try {
      await adminApi.patch(`/admin/timeline/${id}/chart-visibility`);
      load();
    } catch {
      toast.error('Errore');
    }
  };

  const visible = events.filter((e) => filters.includes(e.category));

  return (
    <div className="p-6 md:p-8 lg:p-10" data-testid="admin-timeline-page">
      <PageTooltip page="timeline" />
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-[#1A1A1A]">Eventi importanti</h1>
          <p className="text-[#64748B] text-sm mt-1 max-w-xl">
            Cronologia delle azioni che possono influenzare clienti e ricavi: broadcast, regali,
            coupon, campagne, eventi, modifiche al menu, orari. Aggiungi annotazioni manuali per
            spiegare picchi e cali sui grafici (festa del paese, articolo sul giornale, ecc).
          </p>
        </div>
        <button
          onClick={() => setShowForm((v) => !v)}
          data-testid="timeline-add-annotation-btn"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#D44E20] text-white text-sm font-medium rounded-xl hover:bg-[#B5421B] transition-colors shadow-sm"
        >
          <Plus size={15} />
          {showForm ? 'Chiudi' : 'Aggiungi annotazione'}
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={submitAnnotation}
          data-testid="timeline-annotation-form"
          className="bg-white border border-slate-100 rounded-2xl p-5 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4 shadow-sm"
        >
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-[#64748B] mb-1">Titolo *</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Es. Articolo su La Repubblica"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-[#D44E20] focus:outline-none"
              data-testid="annotation-title-input"
              maxLength={140}
              required
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-[#64748B] mb-1">Descrizione</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Dettagli opzionali sull'evento"
              rows={2}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-[#D44E20] focus:outline-none"
              data-testid="annotation-description-input"
              maxLength={500}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#64748B] mb-1">Data/ora</label>
            <input
              type="datetime-local"
              value={form.occurred_at}
              onChange={(e) => setForm({ ...form, occurred_at: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-[#D44E20] focus:outline-none"
              data-testid="annotation-date-input"
            />
            <p className="text-[10px] text-[#94A3B8] mt-1">Vuoto = adesso</p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-[#64748B] mb-1">Categoria</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-[#D44E20] focus:outline-none"
              data-testid="annotation-category-select"
            >
              {Object.entries(CATEGORY_META).map(([k, meta]) => (
                <option key={k} value={k}>{meta.label}</option>
              ))}
            </select>
          </div>
          <div className="md:col-span-2 flex justify-end">
            <button
              type="submit"
              disabled={saving}
              data-testid="annotation-submit"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A1A1A] text-white text-sm font-medium rounded-xl hover:bg-[#333] disabled:opacity-50 transition-colors"
            >
              <BookmarkPlus size={14} />
              {saving ? 'Salvataggio…' : 'Salva annotazione'}
            </button>
          </div>
        </form>
      )}

      <div className="flex items-center gap-2 flex-wrap mb-5" data-testid="timeline-filters">
        {Object.entries(CATEGORY_META).map(([cat, meta]) => {
          const on = filters.includes(cat);
          return (
            <button
              key={cat}
              type="button"
              onClick={() => toggleCat(cat)}
              data-testid={`filter-${cat}`}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition-opacity ${
                on ? '' : 'opacity-40 hover:opacity-70'
              }`}
              style={{
                color: meta.color,
                backgroundColor: `${meta.color}12`,
                borderColor: `${meta.color}40`,
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: meta.color }} />
              {meta.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-7 h-7 border-2 border-[#D44E20] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : visible.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-slate-200 p-12 text-center" data-testid="timeline-empty">
          <CalendarIcon size={36} className="mx-auto text-slate-300 mb-3" />
          <p className="text-[#64748B] text-sm">Nessun evento ancora — verranno registrati automaticamente quando invii broadcast, crei coupon, modifichi il menu, ecc.</p>
        </div>
      ) : (
        <ol className="relative border-l-2 border-slate-100 ml-3 space-y-4" data-testid="timeline-list-full">
          {visible.map((ev) => {
            const meta = CATEGORY_META[ev.category] || CATEGORY_META.manual;
            const Icon = meta.icon;
            const date = ev.occurred_at ? new Date(ev.occurred_at) : null;
            return (
              <li key={ev.id} className="ml-6" data-testid={`timeline-entry-${ev.id}`}>
                <span
                  className="absolute -left-[13px] w-6 h-6 rounded-full ring-4 ring-white flex items-center justify-center"
                  style={{ backgroundColor: meta.color, color: '#fff' }}
                >
                  <Icon size={12} />
                </span>
                <div className="bg-white rounded-xl p-4 border border-slate-100 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#1A1A1A]">{ev.title}</p>
                      {ev.description && (
                        <p className="text-xs text-[#64748B] mt-1 whitespace-pre-line">{ev.description}</p>
                      )}
                      <div className="flex items-center gap-3 mt-2 text-[11px] text-[#94A3B8]">
                        <span>
                          {date ? date.toLocaleString('it-IT', {
                            day: '2-digit', month: 'short', year: 'numeric',
                            hour: '2-digit', minute: '2-digit',
                          }) : ''}
                        </span>
                        {ev.staff_name && <span>· {ev.staff_name}</span>}
                        <span
                          className="px-1.5 py-0.5 rounded text-[10px] font-medium"
                          style={{ color: meta.color, backgroundColor: `${meta.color}15` }}
                        >
                          {meta.label}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => toggleChartVisibility(ev.id)}
                        title={ev.chart_visible ? 'Nascondi dai grafici' : 'Mostra sui grafici'}
                        data-testid={`toggle-chart-${ev.id}`}
                        className="p-1.5 rounded-lg text-[#64748B] hover:bg-slate-100 hover:text-[#1A1A1A] transition-colors"
                      >
                        {ev.chart_visible ? <Eye size={13} /> : <EyeOff size={13} />}
                      </button>
                      {ev.type === 'manual_annotation' && (
                        <button
                          type="button"
                          onClick={() => deleteEntry(ev.id)}
                          title="Elimina"
                          data-testid={`delete-${ev.id}`}
                          className="p-1.5 rounded-lg text-[#EF4444] hover:bg-red-50 transition-colors"
                        >
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
